import { useState } from 'react';
import { Box, Divider, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LoginIcon from '@mui/icons-material/Login';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { SortableContext, SortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MDButton from '@components/MDButton';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { LeadStatusResponse } from '@layouts/pages/Settings/general/lead-status/services/responses/lead-status.response';
import { UserResponse } from 'layouts/pages/System/users/services/responses/user.response';
import { BoardColumnWithLeads } from '../../services/responses/board-column.response';
import { LeadResponse } from '../../services/responses/lead.response';
import { ScheduleFilter } from '../../filters/crm-schedule-filter';
import { hasLeadFilters, matchesClosedLeadFilters, matchesLeadFilters } from '../../filters/crm-lead-filters';
import { formatRoundedBrl } from '../../formatters/crm-lead-amount';
import { getCrmColors } from '../../crm-theme';
import { CrmLeadCard } from './crm-lead-card';
import { CrmClosedLeads } from './crm-closed-leads';
import { useProfilePermissions } from '@hooks/use-profile-permissions.hook';
import {
    HEADER_SX, LEADS_AREA_SX, addLeadButtonSx, columnSx, columnTotalSx, dropLineSx, leadCountSx,
} from './crm-column.styles';

/// Sem deslocamento dos vizinhos: o dnd-kit só fecha a conta com alturas iguais; o destino é a linha.
const NO_DISPLACEMENT: SortingStrategy = () => null;

interface Props {
    column: BoardColumnWithLeads;
    searchTerm: string;
    statusFilterId: string | null;
    scheduleFilter: ScheduleFilter | null;
    ownerFilter: string | null;
    /// Id da tag ou nulo para todas.
    tagFilter: string | null;
    /// Destaque de uns segundos, quando o painel abre o quadro nesta coluna.
    focused?: boolean;
    statuses: LeadStatusResponse[];
    users?: UserResponse[];
    onAssignLead?: (lead: LeadResponse, userId: string | null) => void;
    onOpenLead: (lead: LeadResponse) => void;
    onChangeLeadStatus: (lead: LeadResponse, statusId: string) => void;
    onOpenSchedule: (lead: LeadResponse) => void;
    onAddLead: (columnId: string) => void;
    onEditColumn: (column: BoardColumnWithLeads) => void;
    onDeleteColumn: (columnId: string) => void;
    onSetEntryColumn: (columnId: string) => void;
    boardActive: boolean;
    focusedLead?: string | null;
    /// A soltura cai nesta coluna, acima de previewBefore; com previewHere, null é o fim da lista.
    previewHere?: boolean;
    previewBefore?: string | null;
}

export const CrmColumn = ({
    column, searchTerm, statusFilterId, scheduleFilter, ownerFilter, tagFilter, focused = false, statuses, users,
    onAssignLead, onOpenLead, onChangeLeadStatus, onOpenSchedule, onAddLead, onEditColumn, onDeleteColumn,
    onSetEntryColumn, boardActive, focusedLead, previewHere = false, previewBefore = null,
}: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const { canManageBoards } = useProfilePermissions();

    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: column.id,
        data: { type: 'column', column },
        disabled: !boardActive || !canManageBoards,
    });

    const filters = { searchTerm, statusFilterId, ownerFilter, tagFilter, scheduleFilter };
    const visibleLeads = column.leads.filter((lead) => matchesLeadFilters(lead, filters));
    const visibleClosed = (column.closedLeads ?? []).filter((lead) => matchesClosedLeadFilters(lead, filters));

    // Lead sem valor entra como zero: a soma é só o que foi estimado.
    const total = column.leads.reduce((sum, lead) => sum + (lead.value ?? 0), 0);

    return (
        <Box
            ref={setNodeRef}
            id={`board-column-${column.id}`}
            style={{ transform: CSS.Translate.toString(transform), transition }}
            sx={columnSx(cc, focused)}
        >
            <Box {...attributes} {...listeners} sx={HEADER_SX}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 160, color: cc.textPrimary }}>
                                {column.name}
                            </Typography>

                            {/* Sem o selo, o papel de porta de entrada seria invisível. */}
                            {column.isEntry && (
                                <Tooltip title={t('crmKanban.colunaDeEntradaAjuda')}>
                                    <LoginIcon sx={{ fontSize: 14, color: cc.accent, flexShrink: 0 }} />
                                </Tooltip>
                            )}
                        </Box>

                        {/* Soma da etapa só com valor lançado: zero fixo diria que a etapa não vale nada. */}
                        {total > 0 && (
                            <Typography variant="caption" sx={columnTotalSx(cc)}>{formatRoundedBrl(total)}</Typography>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="caption" sx={leadCountSx(cc)}>
                            {hasLeadFilters(filters) ? `${visibleLeads.length}/${column.leads.length}` : column.leads.length}
                        </Typography>
                        {canManageBoards && (
                            <IconButton
                                size="small"
                                aria-label={t('crmKanban.opcoesDaColuna')}
                                onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{ p: 0.25 }}
                            >
                                <MoreHorizIcon fontSize="small" sx={{ color: cc.textSecondary }} />
                            </IconButton>
                        )}
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ mx: 1.5, mt: 0.5, borderColor: cc.border }} />

            <Box sx={LEADS_AREA_SX}>
                {previewHere && visibleLeads.length === 0 && <Box sx={dropLineSx(cc.accent)} />}

                <SortableContext items={visibleLeads.map((lead) => lead.id)} strategy={NO_DISPLACEMENT}>
                    {visibleLeads.map((lead, position) => (
                        <Box key={lead.id} sx={{ position: 'relative' }}>
                            {/* Absoluta: o dnd-kit mede os cards só no começo do arraste. */}
                            {previewHere && previewBefore === lead.id && <Box sx={dropLineSx(cc.accent, 'top')} />}
                            {previewHere && previewBefore === null && position === visibleLeads.length - 1 && (
                                <Box sx={dropLineSx(cc.accent, 'bottom')} />
                            )}
                            <CrmLeadCard
                                lead={lead}
                                onOpen={onOpenLead}
                                statuses={statuses}
                                users={users}
                                onAssign={onAssignLead}
                                onChangeStatus={onChangeLeadStatus}
                                onOpenSchedule={onOpenSchedule}
                                boardActive={boardActive}
                                focused={lead.id === focusedLead}
                            />
                        </Box>
                    ))}
                </SortableContext>

                <CrmClosedLeads leads={visibleClosed} onOpen={onOpenLead} />
            </Box>

            <Box sx={{ p: 1 }}>
                <MDButton
                    variant="text"
                    fullWidth
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => boardActive && onAddLead(column.id)}
                    disabled={!boardActive}
                    sx={addLeadButtonSx(cc, controller.darkMode)}
                >
                    {t('crmKanban.adicionarLead')}
                </MDButton>
            </Box>

            {/* Fora da alça: pelo portal, o clique no menu ainda subiria até os listeners do arrasto. */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                onClick={() => setMenuAnchor(null)}
            >
                <MenuItem onClick={() => onEditColumn(column)} disabled={!boardActive}>
                    {t('general.editar')}
                </MenuItem>
                {/* Já é a entrada: repetir a opção seria um clique que não muda nada. */}
                {!column.isEntry && (
                    <MenuItem onClick={() => onSetEntryColumn(column.id)} disabled={!boardActive}>
                        {t('crmKanban.definirComoEntrada')}
                    </MenuItem>
                )}
                <MenuItem onClick={() => onDeleteColumn(column.id)} disabled={!boardActive} sx={{ color: 'error.main' }}>
                    {t('general.excluir')}
                </MenuItem>
            </Menu>
        </Box>
    );
};
